import * as React from "react";
import { styled } from "@mui/material/styles";
import { Chip, Paper, IconButton, Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { OidcIdentityContext } from "@rsmus/react-auth";
import { userAssignmentClientApi } from "../../../api-client-nswag/taxportal-client-runtime";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { getAppName } from "../../../cui/commonComponents/AppNameToggle";
import { snackbarState } from "../../../cui/commonComponents/CustomSnackbar";

interface ChipData {
  key: number;
  label: string;
}

interface ChipProps {
  flag: boolean;
  setFlag: React.Dispatch<React.SetStateAction<boolean>>;
  selectedData: any;
}

const ListItem = styled("li")(({ theme }) => ({
  margin: theme.spacing(0.5),
}));


export default function L2Users({ flag, setFlag, selectedData }: ChipProps) {
  React.useContext(OidcIdentityContext);
  const { user } = React.useContext(OidcIdentityContext);
  const userEmails: string[] = [];
  const appName = useRecoilValue(getAppName);
  const setSnackbar = useSetRecoilState(snackbarState);
  const [deletedEmails, setDeletedEmails] = React.useState<string[]>([]);

  const [chipData, setChipData] = React.useState<ChipData[]>(
    userEmails.map((email, index) => ({ key: index, label: email }))
  );
  const [loading, setLoading] = React.useState(false);
  const [hasUsers, setHasUsers] = React.useState(false);

  const resetState = () => {
    setChipData([]);
    setDeletedEmails([]);
  };

  const loadL2Users = async () => {
    setLoading(true);

    try {
      const response =
        await userAssignmentClientApi.userAssignment_L2SupportUsers(
          selectedData[0].hqSiteId
        );

      const emails: string[] = response?.data ?? [];

      setHasUsers(emails.length > 0);

      setChipData(
        emails.map((email, index) => ({
          key: index,
          label: email,
        }))
      );

      setDeletedEmails([]);
    } catch (err) {
      console.error("Failed to load L2 users", err);
      setHasUsers(false);
      setChipData([]);
    } finally {
      setLoading(false);
    }
  };


  React.useEffect(() => {
    if (!flag) return;
    loadL2Users();

  }, [flag, selectedData]);

  React.useEffect(() => {
    if (!flag) {
      resetState();
    }
  }, [flag]);

  const handleDelete = (chipToDelete: ChipData) => {
    // remove from UI
    setChipData((prev) =>
      prev.filter((chip) => chip.key !== chipToDelete.key)
    );

    // track deleted emails
    setDeletedEmails((prev) => [...prev, chipToDelete.label]);
  };

  const handleDeleteUsers = async () => {
    setFlag(false);

    const roleName = appName === "CS" ? "CS - L2 Support" : "L2Support";
    setSnackbar({ open: true, message: `Unassigning user to ${roleName} in progress...`, severity: "info" });

    try {
      const stepName = "DirectRoleAssignInitiate";
      const emails = chipData.map((item) => item.label);
      if (emails.length === 0) {
        setSnackbar({
          open: true,
          message: "No users selected for removal",
          severity: "info",
        });
        return;
      }
      const failures: string[] = [];
      for (const email of emails) {
        const payload = {
          hqSiteId: selectedData[0].hqSiteId,
          userEmail: email,
          roleName: roleName,
          action: 1, // UserAssignmentCEMAction.Remove
          appName: appName,
          initiatedBy: user?.profile.preferred_username || "",
          userAssignmentStepName: stepName,
          Source: "TPAC",
        } as any;

        try {
          console.log("Unassigning user with payload:", payload);
          await userAssignmentClientApi.userAssignment_DirectRoleAssignInitiate(payload);
        } catch {

          failures.push(email);
        }
      }

      if (failures.length === 0) {
        setSnackbar({ open: true, message: `User Assignment removed successful`, severity: "success" });
      } else {
        setSnackbar({ open: true, message: `User Assignment removal failed`, severity: "error" });
      }
    } catch {
      setSnackbar({ open: true, message: `User Assignment removal failed`, severity: "error" });
    } finally {
      setDeletedEmails([]);
    }
  };

  const cancelAction = async () => {
    await loadL2Users();
    //setFlag(false);      
  };


  return (
    <Dialog open={flag} onClose={() => setFlag(false)} fullWidth maxWidth="sm" className="cui-c_modal">
      <DialogTitle className="cui-c_modal-title">Unassign L2Support</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => {
          // resetState();
          setFlag(false);
        }}
        sx={{ position: "absolute", right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
      >
        <CloseIcon />
      </IconButton>


      <DialogContent sx={{ padding: "0 16px 10px 16px" }}>
        {loading ? (
          <p style={{ textAlign: "center", marginTop: 16 }}>
            Loading L2 Support users...
          </p>
        ) : hasUsers ? (
          <Paper
            sx={{
              display: "flex",
              justifyContent: "center",
              flexWrap: "wrap",
              listStyle: "none",
              p: 0.5,
              m: 1,
            }}
            component="ul"
          >
            {chipData.map((data) => (
              <ListItem key={data.key}>
                <Chip label={data.label} onDelete={() => handleDelete(data)} />
              </ListItem>
            ))}
          </Paper>
        ) : (
          <p style={{ textAlign: "center", marginTop: 16 }}>
            No L2 Support users exist for this site.
          </p>
        )}
      </DialogContent>


      <DialogActions className="cui-c_modal-footer">
        {loading ? (
          <Button disabled variant="outlined">
            Loading...
          </Button>
        ) : hasUsers ? (
          <>
            <Button onClick={cancelAction} variant="outlined">
              Cancel
            </Button>
            <Button onClick={handleDeleteUsers} variant="outlined">
              Remove User(s)
            </Button>
          </>
        ) : (
          <Button onClick={() => setFlag(false)} variant="outlined">
            Close
          </Button>
        )}
      </DialogActions>

    </Dialog>
  );
}